<script lang="ts" setup>
interface ButtonProps {
  schema?: 'primary' | 'outline' | 'disabled' | 'secondary' | 'right' | 'transparent' | 'icon' | 'text' | 'error';
  outline?: boolean;
  background?: string;
  secondary?: boolean;
  disabled?: boolean;
  right?: boolean;
  type?: 'button' | 'submit' | 'reset';
  iconClassLeft?: string;
  iconClassRight?: string;
  small?: boolean;
  medium?: boolean;
  loading?: boolean;
}

const props = withDefaults(defineProps<ButtonProps>(), {
  schema: 'primary',
  background: '',
  type: 'button',
  iconClassLeft: undefined,
  iconClassRight: undefined,
  small: false,
  medium: false,
});

const cClass = computed(() => {
  const res = ['new-button_' + props.schema];
  if (props.outline) {
    res.push('new-button_outline');
  }
  if (props.disabled) {
    res.push('new-button_disabled');
  }
  if (props.secondary) {
    res.push('new-button_secondary');
  }
  if (props.right) {
    res.push('new-button_right');
  }
  if (props.small) {
    res.push('new-button_sm');
  }
  if (props.medium) {
    res.push('new-button_md');
  }
  return res.join(' ');
});
</script>

<template>
  <button
    :class="cClass"
    :disabled="disabled || loading"
    :type="type"
    class="new-button"
  >
    <span
      v-if="loading"
      class="new-loader"
    />
    <span
      v-if="iconClassLeft"
      class="new-button__icon-left"
    >
      <i
        :class="iconClassLeft"
        class="icon"
      />
    </span>
    <slot name="iconLeft" />
    <slot />
    <slot name="iconRight" />
    <span
      v-if="iconClassRight"
      class="new-button__icon-right"
    >
      <i
        :class="iconClassRight"
        class="icon"
      />
    </span>
  </button>
</template>

<style lang="scss">
.new-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 24px;
  border-radius: 12px;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  line-height: 26px;
  border: none;
  cursor: pointer;
  transition: all 300ms;

  &:hover {
    text-decoration: none;
  }

  &.new-button_sm {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    padding: 10px 16px;
  }

  &.new-button_default {
    @include media-breakpoint-down(sm) {
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
    }
  }

  &.new-button_md {
    font-size: 16px;
    font-weight: 500;
    line-height: 28px;
    height: 56px;
  }

  &.new-button_right {
    border-radius: 0 12px 12px 0;
  }

  &.new-button_disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &.new-button_text {
    padding: 0;
    color: $foreground-theme;
    font-weight: 500;
    display: inline;
    font-size: 14px;
    line-height: 20px;
    background: transparent;
  }

  &.new-button_error {
    color: $danger;
    background-color: rgba($danger, 0.2);

    &:hover {
      background-color: rgba($danger, 0.1);
    }
    &:active {
      background-color: rgba($danger, 0.3);
    }
    &:focus {
      outline: 4px solid $icon-theme-fade;
      background-color: rgba($danger, 0.3);
    }
    &:disabled {
      opacity: 40%;
      background-color: rgba($danger, 0.3);
    }
  }

  &:focus {
    outline: none;
  }
}

.new-loader {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-block;
  position: relative;
  margin: auto 0;

  box-sizing: border-box;
  animation: rotation 1s linear infinite;

  &::after {
    content: '';
    box-sizing: border-box;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 16px;
    height: 16px;
    border-radius: 50%;
  }
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.new-button__icon-left,
.new-button__icon-right {
  //margin-right: 8px;
  display: flex;
  align-items: center;
}

.new-button_primary {
  color: #fff;
  background: $foreground-theme;

  &:hover {
    color: #fff;
    background: #6059e9;
  }

  .new-loader {
    background: linear-gradient(0deg, rgb(255 255 255 / 20%) 33%, #ffffff 100%);

    &::after {
      background: $foreground-theme;
    }
  }
}

.new-button_secondary {
  color: $foreground-theme;
  background: $background-theme-fade;

  &:hover {
    background: rgba($foreground-theme, 0.1);
  }
  .new-loader {
    background: linear-gradient(0deg, rgba($foreground-theme, 0) 33%, $foreground-theme 100%);

    &::after {
      background: $background-theme-fade;
    }
  }
}

.new-button_outline {
  color: $foreground-contrast;
  background: $background-theme-fade;

  &:hover {
    color: $foreground-contrast;
    background: $background-button-secondary-hover;
  }
}

.new-button_transparent {
  color: $foreground-contrast !important;
  border: none;
  background: transparent;

  &:hover {
    background: $background-button-secondary-hover;
  }
}

.new-button_icon {
  padding: 0;
  border: none;
  background: transparent;
}

.new-button_transparent {
  color: $foreground-theme;
  border: none;
  background: transparent;
}

.new-button_icon {
  padding: 0;
  border: none;
  background: transparent;
}
</style>
